import { images } from "@/utils/data"
import { mapDesigns } from "@mono/mapDesigns"
import ModalCloseButton from "@/Components/ModalCloseButton"
import { useBackHandler } from "@/Hooks/useBackHandler"

interface SelectMapModalProps {
  isOpen: boolean
  onClose: () => void
  selectedMapId?: string
  onSelectMap: (mapId: string) => void
}

const SelectMapModal: React.FC<SelectMapModalProps> = ({
  isOpen,
  onClose,
  selectedMapId,
  onSelectMap,
}) => {
  // Before the early return: hooks cannot run conditionally.
  useBackHandler(() => {
    onClose()
    return true
  }, isOpen)

  if (!isOpen) return null

  const handleMapSelect = (mapId: string) => {
    onSelectMap(mapId)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="relative p-8 w-full max-w-6xl max-h-[90vh] flex flex-col rounded-3xl overflow-hidden border border-amber-400/20 bg-gradient-to-b from-gray-900 via-gray-950 to-black shadow-[0_0_80px_rgba(251,191,36,0.12)]">
        <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent rounded-full" />
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-2xl font-bold text-white font-fourth">
            Select Map
          </h3>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-y-auto overflow-x-hidden flex-1 min-h-0 py-4">
          {mapDesigns.map((map) => (
            <div
              key={map.id}
              onClick={() => handleMapSelect(map.id)}
              className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(255,255,255,0.06)] ${
                selectedMapId === map.id
                  ? "border-blue-500 bg-gradient-to-r from-blue-500/20 to-indigo-500/20"
                  : "border-[#23201b] bg-[#0c0c0b] hover:border-[#333]"
              }`}
            >
              <div className="flex flex-col items-center gap-4 mb-4">
                <img
                  //@ts-ignore
                  src={images.maps[map.id]}
                  alt={map.name}
                  className="w-60 h-60 rounded-lg object-cover border border-[#23201b]"
                />

                <div className="text-center">
                  <h4 className="text-xl font-bold text-white mb-2">
                    {map.name}
                  </h4>
                  <div className="text-gray-400 text-sm">
                    {map.cells.length} cells • {map.groups.length} property
                    groups
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-gray-300 text-sm">
                  <strong>Properties:</strong>{" "}
                  {map.cells.filter((cell) => cell.type === "property").length}
                </div>
                <div className="text-gray-300 text-sm">
                  <strong>Stations:</strong>{" "}
                  {
                    map.cells.filter(
                      (cell) =>
                        cell.type === "property" &&
                        cell.propertyDetails?.groupId === "8"
                    ).length
                  }
                </div>
                <div className="text-gray-300 text-sm">
                  <strong>Actions:</strong>{" "}
                  {map.cells.filter((cell) => cell.type === "action").length}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-6">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-semibold transition-all duration-200 rounded-xl hover:scale-105 active:scale-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default SelectMapModal
